import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Card,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CustomFilterV2, TableV2 } from '../../../components/v2';
import { tableV2LoadingLinearSx } from '../../../components/v2/tableV2Styles';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import * as actionsCalendarioFeriado from '../../../store/calendarioFeriado/actions';
import * as selectorsCalendarioFeriado from '../../../store/calendarioFeriado/reducer';
import CalendarGrid from '../Calendario/CalendarGrid';
import CalendarioTipoLegend from '../Calendario/CalendarioTipoLegend';
import {
  buildMonthDays,
  createDefaultLegendFilter,
  filterFeriadosByLegend,
  filterFeriadosByMunicipiosCurados,
  groupFeriadosByDate,
} from '../Calendario/dateUtils';
import Formulario from './Formulario';
import InvalidarDialog from './InvalidarDialog';

moment.locale('pt-br');

const TIPO_LABEL = {
  NACIONAL: 'Nacional',
  ESTADUAL: 'Estadual',
  MUNICIPAL: 'Municipal',
  DAY_OFF_ANIVERSARIO: 'Day off aniversário',
};

const STATUS_LABEL = {
  VALIDO: 'Válido',
  INVALIDO: 'Inválido',
};

const TIPO_OPTIONS = [
  { value: 'NACIONAL', label: 'Nacional' },
  { value: 'ESTADUAL', label: 'Estadual' },
  { value: 'MUNICIPAL', label: 'Municipal' },
  { value: 'DAY_OFF_ANIVERSARIO', label: 'Day off aniversário' },
];

const STATUS_OPTIONS = [
  { value: 'VALIDO', label: 'Válido' },
  { value: 'INVALIDO', label: 'Inválido' },
];

const ORIGEM_LABEL = {
  AUTOMATICO: 'Automática',
  MANUAL: 'Manual',
};

const ORIGEM_OPTIONS = [
  { value: 'AUTOMATICO', label: 'Automática' },
  { value: 'MANUAL', label: 'Manual' },
];

const ESCOPO_MUNICIPIOS_OPTIONS = [
  { value: 'COLABORADORES', label: 'Com colaboradores' },
  { value: 'TODOS', label: 'Todos da UF' },
];

const UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
].map((uf) => ({ value: uf, label: uf }));

const MES_OPTIONS = [
  { value: '', label: 'Todos' },
  ...moment.months().map((name, index) => ({
    value: index + 1,
    label: name.charAt(0).toUpperCase() + name.slice(1),
  })),
];

const toMonthNumber = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 1 && n <= 12 ? n : null;
};

const normalizeMesIntervalo = (mesInicio, mesFim) => {
  let inicio = toMonthNumber(mesInicio);
  let fim = toMonthNumber(mesFim);
  if (inicio != null && fim != null && inicio > fim) {
    const tmp = inicio;
    inicio = fim;
    fim = tmp;
  }
  return { mesInicio: inicio, mesFim: fim };
};

const buildDefaultFilter = () => ({
  ano: moment().year(),
  mesInicio: moment().month() + 1,
  mesFim: 12,
  uf: 'MG',
  codigoIbges: null,
  tipos: null,
  origens: null,
  statuses: null,
  escopoMunicipios: 'COLABORADORES',
});

/** Normaliza multiSelect: array vazio / null → null (sem filtro). */
const toMultiFilterValue = (value) => {
  if (value == null) return null;
  const list = Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
  return list.length ? list : null;
};

/** Escopo colaboradores só quando não há município explícito. */
const resolveSomenteMunicipiosComUsuarios = (filter) => {
  if (toMultiFilterValue(filter?.codigoIbges)) return false;
  return filter?.escopoMunicipios !== 'TODOS';
};

/** Filtro da Lista — intervalo mesInicio/mesFim, sem `mes` único. */
const toApiFilter = (filter) => {
  const { mesInicio, mesFim } = normalizeMesIntervalo(filter?.mesInicio, filter?.mesFim);
  return {
    ano: filter?.ano ?? null,
    mesInicio,
    mesFim,
    uf: filter?.uf || null,
    codigoIbges: toMultiFilterValue(filter?.codigoIbges),
    tipos: toMultiFilterValue(filter?.tipos),
    origens: toMultiFilterValue(filter?.origens),
    statuses: toMultiFilterValue(filter?.statuses),
    somenteMunicipiosComUsuarios: resolveSomenteMunicipiosComUsuarios(filter),
  };
};

const formatMonthShort = (mes) => {
  const monthIndex = Number(mes) - 1;
  if (monthIndex < 0 || monthIndex > 11) return String(mes);
  const name = moment.monthsShort()[monthIndex];
  return name.charAt(0).toUpperCase() + name.slice(1).replace('.', '');
};

const formatPeriodoChipLabel = (mesInicio, mesFim, ano) => {
  const inicio = toMonthNumber(mesInicio);
  const fim = toMonthNumber(mesFim);
  if (inicio == null && fim == null) return 'todos';
  if (inicio != null && fim != null) {
    if (inicio === fim) {
      return ano ? `${formatMonthShort(inicio)}/${ano}` : formatMonthShort(inicio);
    }
    const range = `${formatMonthShort(inicio)}–${formatMonthShort(fim)}`;
    return ano ? `${range}/${ano}` : range;
  }
  if (inicio != null) {
    return ano ? `a partir de ${formatMonthShort(inicio)}/${ano}` : `a partir de ${formatMonthShort(inicio)}`;
  }
  return ano ? `até ${formatMonthShort(fim)}/${ano}` : `até ${formatMonthShort(fim)}`;
};

const filterFeriadosByMesIntervalo = (feriados, ano, mesInicio, mesFim) => {
  const inicio = toMonthNumber(mesInicio);
  const fim = toMonthNumber(mesFim);
  if (inicio == null && fim == null) return feriados || [];
  const anoNum = ano != null ? Number(ano) : null;
  return (feriados || []).filter((f) => {
    if (!f?.data) return false;
    const m = moment(f.data);
    if (!m.isValid()) return false;
    if (anoNum != null && m.year() !== anoNum) return false;
    const month = m.month() + 1;
    if (inicio != null && month < inicio) return false;
    if (fim != null && month > fim) return false;
    return true;
  });
};

const formatMultiChipLabel = (values, labelMap) => {
  const list = toMultiFilterValue(values);
  if (!list) return 'todos';
  return list.map((v) => labelMap[v] || v).join(', ');
};

const viewModeToggleSx = {
  height: 36.5,
  mr: 2,
  alignSelf: 'center',
  '& .MuiToggleButtonGroup-grouped': {
    borderColor: 'divider',
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderLeft: '1px solid',
      borderLeftColor: 'divider',
    },
    '&.Mui-selected + .MuiToggleButtonGroup-grouped': {
      borderLeftColor: 'primary.main',
    },
  },
  '& .MuiToggleButton-root': {
    px: 1.25,
    py: 0,
    height: 36.5,
    gap: 0.5,
    minWidth: 'auto',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    textTransform: 'none',
    color: 'text.secondary',
    border: '1px solid',
    borderColor: 'divider',
    '&.Mui-selected': {
      zIndex: 1,
      color: 'primary.main',
      bgcolor: 'action.selected',
      border: '1px solid',
      borderColor: 'primary.main',
      '&:hover': {
        bgcolor: 'action.selected',
      },
    },
  },
};

const ViewModeToggle = ({ value, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value}
    onChange={onChange}
    aria-label="Modo de visualização"
    sx={viewModeToggleSx}
  >
    <ToggleButton value="lista" aria-label="Lista">
      <ViewListOutlinedIcon sx={{ fontSize: 16 }} />
      Lista
    </ToggleButton>
    <ToggleButton value="calendario" aria-label="Calendário">
      <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
      Calendário
    </ToggleButton>
  </ToggleButtonGroup>
);

ViewModeToggle.propTypes = {
  value: PropTypes.oneOf(['lista', 'calendario']).isRequired,
  onChange: PropTypes.func.isRequired,
};

const CalendarioAdmin = ({
  feriadosAdmin,
  loadingCalendarioAdmin,
  municipiosUsuariosAtivos,
  invalidarFeriado,
  revalidarFeriado,
  loadCalendarioAdminCalendar,
  loadMunicipiosUsuariosAtivos,
  cleanCalendarioFeriado,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [openInvalidar, setOpenInvalidar] = useState(false);
  const [invalidarRow, setInvalidarRow] = useState(null);
  const [reload, setReload] = useState(false);
  const [viewMode, setViewMode] = useState('calendario');
  const [referenceDate, setReferenceDate] = useState(moment());
  const [legendFilter, setLegendFilter] = useState(createDefaultLegendFilter);
  const [defaultFilter] = useState(buildDefaultFilter);
  const [localFilter, setLocalFilter] = useState(defaultFilter);

  const apiFilter = useMemo(() => toApiFilter(localFilter), [localFilter]);

  const calendarApiFilter = useMemo(() => {
    const {
      mesInicio: _mesInicio,
      mesFim: _mesFim,
      ...rest
    } = apiFilter;
    return {
      ...rest,
      ano: referenceDate.year(),
      mes: referenceDate.month() + 1,
      mesInicio: null,
      mesFim: null,
    };
  }, [apiFilter, referenceDate]);

  /** Fetch sem paginação para Lista com escopo local — usa intervalo, sem `mes`. */
  const listUnpagedFilter = useMemo(() => ({
    ...apiFilter,
    mes: null,
  }), [apiFilter]);

  const municipioOptions = useMemo(() => {
    const list = (municipiosUsuariosAtivos || []).filter((m) => m?.codigoIbge);
    const scoped = localFilter.uf
      ? list.filter((m) => !m.uf || m.uf === localFilter.uf)
      : list;
    return scoped.map((m) => ({
      value: String(m.codigoIbge),
      label: m.municipioNome && m.uf
        ? `${m.municipioNome} / ${m.uf}`
        : (m.municipioNome || String(m.codigoIbge)),
    }));
  }, [municipiosUsuariosAtivos, localFilter.uf]);

  // Baseado na lista bruta do servidor (não no scoped por UF): UF sem municípios
  // curados mostra select vazio, sem cair no catálogo nacional (~5.570).
  const useCuratedMunicipios = (municipiosUsuariosAtivos || []).some((m) => m?.codigoIbge);

  const municipioChipLabel = useMemo(() => {
    const selected = toMultiFilterValue(localFilter.codigoIbges);
    if (!selected) return 'todos';
    if (selected.length === 1) {
      const match = municipioOptions.find((o) => o.value === String(selected[0]));
      return match?.label || selected[0];
    }
    return `${selected.length} selecionados`;
  }, [localFilter.codigoIbges, municipioOptions]);

  const municipioIbgeSet = useMemo(() => {
    const set = new Set();
    (municipiosUsuariosAtivos || []).forEach((m) => {
      if (m?.codigoIbge) set.add(String(m.codigoIbge));
    });
    return set;
  }, [municipiosUsuariosAtivos]);

  const selectedMunicipioIbgeSet = useMemo(() => {
    const selected = toMultiFilterValue(localFilter.codigoIbges);
    if (!selected) return null;
    return new Set(selected.map(String));
  }, [localFilter.codigoIbges]);

  const applyColaboradoresScope = useMemo(
    () => resolveSomenteMunicipiosComUsuarios(localFilter) && municipioIbgeSet.size > 0,
    [localFilter, municipioIbgeSet]
  );

  const useLocalScopedRows = useMemo(
    () => applyColaboradoresScope || !!selectedMunicipioIbgeSet || !!toMultiFilterValue(localFilter.tipos),
    [applyColaboradoresScope, selectedMunicipioIbgeSet, localFilter.tipos]
  );

  useEffect(() => {
    loadMunicipiosUsuariosAtivos();
    return () => {
      cleanCalendarioFeriado();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewMode === 'calendario') {
      loadCalendarioAdminCalendar(calendarApiFilter);
      return;
    }
    if (useLocalScopedRows) {
      loadCalendarioAdminCalendar(listUnpagedFilter);
    }
  }, [
    viewMode,
    calendarApiFilter,
    listUnpagedFilter,
    reload,
    loadCalendarioAdminCalendar,
    useLocalScopedRows,
  ]);

  useEffect(() => {
    const anoFiltro = localFilter.ano || moment().year();
    setReferenceDate((prev) => {
      if (prev.year() === anoFiltro) return prev;
      return moment(prev).year(anoFiltro);
    });
  }, [localFilter.ano]);

  const feriadosBaseScoped = useMemo(() => {
    let list = feriadosAdmin || [];
    const tipos = toMultiFilterValue(localFilter.tipos);
    if (tipos) {
      list = list.filter((f) => tipos.includes(f.tipo));
    }
    if (selectedMunicipioIbgeSet) {
      list = list.filter((f) => {
        if (!f.codigoIbge) return true;
        return selectedMunicipioIbgeSet.has(String(f.codigoIbge));
      });
    } else if (applyColaboradoresScope) {
      list = filterFeriadosByMunicipiosCurados(list, municipioIbgeSet);
    }
    return list;
  }, [
    feriadosAdmin,
    localFilter.tipos,
    selectedMunicipioIbgeSet,
    applyColaboradoresScope,
    municipioIbgeSet,
  ]);

  const feriadosAdminScoped = useMemo(() => {
    if (viewMode === 'calendario') return feriadosBaseScoped;
    return filterFeriadosByMesIntervalo(
      feriadosBaseScoped,
      localFilter.ano,
      localFilter.mesInicio,
      localFilter.mesFim
    );
  }, [
    viewMode,
    feriadosBaseScoped,
    localFilter.ano,
    localFilter.mesInicio,
    localFilter.mesFim,
  ]);

  const listaRows = useMemo(() => {
    if (!useLocalScopedRows) return [];
    return [...feriadosAdminScoped].sort((a, b) =>
      String(a.data || '').localeCompare(String(b.data || ''))
    );
  }, [useLocalScopedRows, feriadosAdminScoped]);

  const feriadosByDate = useMemo(
    () => groupFeriadosByDate(filterFeriadosByLegend(feriadosBaseScoped, legendFilter)),
    [feriadosBaseScoped, legendFilter]
  );

  const days = useMemo(() => buildMonthDays(referenceDate), [referenceDate]);

  const periodoLabel = useMemo(() => referenceDate.format('MMMM YYYY'), [referenceDate]);

  const navigateMonth = useCallback((delta) => {
    setReferenceDate((prev) => moment(prev).add(delta, 'month'));
  }, []);

  const goToToday = useCallback(() => {
    setReferenceDate(moment());
  }, []);

  const onAdd = () => {
    setSelectedRow(null);
    setOpen(true);
  };

  const onEdit = (rowId, row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const onClose = (isSave) => {
    setOpen(false);
    setSelectedRow(null);
    if (isSave) setReload((r) => !r);
  };

  const handleViewModeChange = (_event, next) => {
    if (next) setViewMode(next);
  };

  const rowActions = [
    {
      key: 'invalidar',
      icon: <NotInterestedIcon fontSize="small" sx={{ color: 'error.main' }} />,
      title: 'Invalidar',
      show: (row) => row.status === 'VALIDO',
      onClick: (rowId, row) => {
        setInvalidarRow(row);
        setOpenInvalidar(true);
      },
    },
    {
      key: 'revalidar',
      icon: <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />,
      title: 'Revalidar',
      show: (row) => row.status === 'INVALIDO',
      onClick: (rowId) => {
        revalidarFeriado(rowId, (ok) => {
          if (ok) setReload((r) => !r);
        });
      },
    },
  ];

  const filterFields = [
    { field: 'useAno', md: 4 },
    { field: 'useSearchSelect', key: 'mesInicio', label: 'Mês início', md: 4, options: MES_OPTIONS },
    { field: 'useSearchSelect', key: 'mesFim', label: 'Mês fim', md: 4, options: MES_OPTIONS },
    { field: 'useSearchSelect', key: 'uf', label: 'UF', md: 3, options: UF_OPTIONS },
    {
      field: 'useSearchSelect',
      key: 'escopoMunicipios',
      label: 'Escopo municípios',
      md: 3,
      options: ESCOPO_MUNICIPIOS_OPTIONS,
    },
    useCuratedMunicipios
      ? {
        field: 'useSearchSelect',
        key: 'codigoIbges',
        label: 'Municípios',
        md: 6,
        options: municipioOptions,
        multiSelect: true,
      }
      : {
        field: 'useMunicipio',
        md: 6,
        multiSelect: true,
        filterKey: 'codigoIbges',
        key: 'codigoIbges',
        label: 'Municípios',
      },
    {
      field: 'useSearchSelect',
      key: 'tipos',
      label: 'Tipo',
      md: 6,
      options: TIPO_OPTIONS,
      multiSelect: true,
    },
    { field: 'useSearchSelect', key: 'origens', label: 'Origem', md: 6, options: ORIGEM_OPTIONS, multiSelect: true },
    { field: 'useSearchSelect', key: 'statuses', label: 'Status', md: 6, options: STATUS_OPTIONS, multiSelect: true },
  ];

  const calendarLoadingSx = {
    opacity: loadingCalendarioAdmin ? 0.38 : 1,
    transition: (theme) =>
      theme.transitions.create('opacity', {
        duration: theme.transitions.duration.shorter,
      }),
    pointerEvents: loadingCalendarioAdmin ? 'none' : 'auto',
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="h4">Calendário Administrativo e Gestão de Feriados</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`Ano: ${localFilter.ano ?? 'todos'}`} onClick={() => setOpenFilter(true)} />
          <Chip
            size="small"
            label={`Período: ${formatPeriodoChipLabel(localFilter.mesInicio, localFilter.mesFim, localFilter.ano)}`}
            onClick={() => setOpenFilter(true)}
          />
          <Chip size="small" label={`UF: ${localFilter.uf || 'todas'}`} onClick={() => setOpenFilter(true)} />
          <Chip
            size="small"
            label={
              toMultiFilterValue(localFilter.codigoIbges)
                ? 'Escopo: municípios'
                : localFilter.escopoMunicipios === 'TODOS'
                  ? 'Escopo: todos da UF'
                  : 'Escopo: colaboradores'
            }
            onClick={() => setOpenFilter(true)}
          />
          <Chip size="small" label={`Municípios: ${municipioChipLabel}`} onClick={() => setOpenFilter(true)} />
          <Chip
            size="small"
            label={`Tipo: ${formatMultiChipLabel(localFilter.tipos, TIPO_LABEL)}`}
            onClick={() => setOpenFilter(true)}
          />
          <Chip
            size="small"
            label={`Origem: ${formatMultiChipLabel(localFilter.origens, ORIGEM_LABEL)}`}
            onClick={() => setOpenFilter(true)}
          />
          <Chip
            size="small"
            label={`Status: ${formatMultiChipLabel(localFilter.statuses, STATUS_LABEL)}`}
            onClick={() => setOpenFilter(true)}
          />
        </Stack>

        {viewMode === 'lista' ? (
          <TableV2
            {...(useLocalScopedRows
              ? {
                rows: listaRows,
                loading: loadingCalendarioAdmin,
              }
              : {
                useApi: true,
                fetchUrl: 'calendario/filter',
                filterData: apiFilter,
                refreshKey: reload,
              })}
            headerActions={[
              {
                key: 'view-mode',
                node: (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', height: 36.5 }}>
                    <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
                  </Box>
                ),
              },
              { label: 'Novo', onClick: onAdd, startIcon: <AddIcon />, variant: 'contained' },
            ]}
            actions={rowActions}
            columns={[
              {
                field: 'data',
                headerName: 'Data',
                width: 120,
                valueGetter: (_value, row) => row?.data || '',
                renderCell: ({ row }) =>
                  row.data ? moment(row.data).format('DD/MM/YYYY') : '—',
              },
              { field: 'nome', headerName: 'Nome', flex: 1, minWidth: 180 },
              {
                field: 'tipo',
                headerName: 'Tipo',
                width: 130,
                renderCell: ({ row }) => TIPO_LABEL[row.tipo] || row.tipo,
              },
              {
                field: 'municipioUf',
                headerName: 'Município/UF',
                flex: 0.8,
                minWidth: 160,
                valueGetter: (_value, row) =>
                  row?.codigoIbge
                    ? `${row.municipioNome || ''} / ${row.uf || ''}`
                    : (row?.uf || ''),
                renderCell: ({ row }) =>
                  row.codigoIbge ? `${row.municipioNome || ''} / ${row.uf || ''}` : (row.uf || '—'),
              },
              {
                field: 'status',
                headerName: 'Status',
                width: 140,
                renderCell: ({ row }) =>
                  renderStatus({
                    value: row.status,
                    label: row.status === 'VALIDO' ? 'Válido' : 'Inválido',
                  }),
              },
              {
                field: 'origem',
                headerName: 'Origem',
                width: 130,
                renderCell: ({ row }) => ORIGEM_LABEL[row.origem] || row.origem || '—',
              },
              {
                field: 'atualizadoEm',
                headerName: 'Última alteração',
                flex: 0.8,
                minWidth: 200,
                renderCell: ({ row }) =>
                  row.atualizadoEm
                    ? `${moment(row.atualizadoEm).format('DD/MM/YYYY')} — ${row.usuarioNome || '—'}`
                    : '—',
              },
            ]}
            onEdit={onEdit}
          />
        ) : (
          <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            {loadingCalendarioAdmin && (
              <LinearProgress
                sx={{
                  ...tableV2LoadingLinearSx,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                }}
              />
            )}
            <Stack spacing={2} sx={{ p: 2, ...calendarLoadingSx }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton size="small" onClick={() => navigateMonth(-1)} aria-label="Mês anterior">
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography
                    variant="subtitle1"
                    sx={{ minWidth: 160, textAlign: 'center', textTransform: 'capitalize', fontWeight: 600 }}
                  >
                    {periodoLabel}
                  </Typography>
                  <IconButton size="small" onClick={() => navigateMonth(1)} aria-label="Próximo mês">
                    <ChevronRightIcon />
                  </IconButton>
                  <Button size="small" onClick={goToToday}>
                    Hoje
                  </Button>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0}>
                  <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onAdd}
                  >
                    Novo
                  </Button>
                </Stack>
              </Stack>

              <CalendarGrid
                days={days}
                referenceDate={referenceDate}
                feriadosByDate={feriadosByDate}
                viewMode="month"
                showLocalidade={!toMultiFilterValue(localFilter.codigoIbges) || toMultiFilterValue(localFilter.codigoIbges).length > 1}
              />

              <CalendarioTipoLegend
                value={legendFilter}
                onChange={setLegendFilter}
                sx={{ mt: 0, pt: 1.5 }}
              />

              <Typography variant="caption" color="text.secondary">
                Visualização apenas — para editar ou invalidar, use o modo Lista.
              </Typography>
            </Stack>
          </Card>
        )}

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter}
          appliedFilter={localFilter}
          fields={filterFields}
          onFilter={(filterData) => {
            const { mesInicio, mesFim } = normalizeMesIntervalo(
              filterData.mesInicio,
              filterData.mesFim
            );
            const next = {
              ...defaultFilter,
              ...filterData,
              ano: filterData.ano ?? defaultFilter.ano,
              mesInicio,
              mesFim,
              escopoMunicipios: filterData.escopoMunicipios || defaultFilter.escopoMunicipios,
              tipos: toMultiFilterValue(filterData.tipos),
              codigoIbges: toMultiFilterValue(filterData.codigoIbges),
              origens: toMultiFilterValue(filterData.origens),
              statuses: toMultiFilterValue(filterData.statuses),
            };
            setLocalFilter(next);
            const ano = next.ano ?? defaultFilter.ano;
            setReferenceDate((prev) => {
              if (prev.year() === ano) return prev;
              return moment(prev).year(ano);
            });
            setOpenFilter(false);
          }}
        />

        <Formulario
          id={selectedRow?.id || null}
          row={selectedRow}
          open={open}
          onClose={onClose}
        />

        <InvalidarDialog
          open={openInvalidar}
          feriado={invalidarRow}
          onClose={() => { setOpenInvalidar(false); setInvalidarRow(null); }}
          onConfirm={(motivo) => {
            invalidarFeriado(invalidarRow.id, motivo, (ok) => {
              if (ok) setReload((r) => !r);
            });
            setOpenInvalidar(false);
            setInvalidarRow(null);
          }}
        />
      </Stack>
    </Box>
  );
};

CalendarioAdmin.propTypes = {
  feriadosAdmin: PropTypes.array,
  loadingCalendarioAdmin: PropTypes.bool,
  municipiosUsuariosAtivos: PropTypes.array,
  invalidarFeriado: PropTypes.func.isRequired,
  revalidarFeriado: PropTypes.func.isRequired,
  loadCalendarioAdminCalendar: PropTypes.func.isRequired,
  loadMunicipiosUsuariosAtivos: PropTypes.func.isRequired,
  cleanCalendarioFeriado: PropTypes.func.isRequired,
};

CalendarioAdmin.defaultProps = {
  feriadosAdmin: [],
  loadingCalendarioAdmin: false,
  municipiosUsuariosAtivos: [],
};

const mapStateToProps = (state) => ({
  feriadosAdmin: selectorsCalendarioFeriado.getFeriadosAdmin(state),
  loadingCalendarioAdmin: selectorsCalendarioFeriado.getLoadingCalendarioAdmin(state),
  municipiosUsuariosAtivos: selectorsCalendarioFeriado.getMunicipiosUsuariosAtivos(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...actionsCalendarioFeriado,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CalendarioAdmin));
